import { ChevronDown } from 'lucide-react';
import { useCallback, useState, type ComponentType } from 'react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useVisualization } from '@/context/visualization-context';
import { useColor3D } from '@/hooks/use-color-3d';
import { cn } from '@/lib/utils';
import type { Color3DPresetId } from '@/visualization/services/color-3d-presets';
import { COLOR_3D_LEVEL_RANGE, getSelectableColor3DPresets } from '@/visualization/services/color-3d-presets';
import { Color3DService } from '@/visualization/services/color-3d-service';

/**
 * Step of Endosize's "3D Colors" slider. Its bounds come from
 * COLOR_3D_LEVEL_RANGE, the same range Color3DService clamps the drag tool to,
 * so the slider can never show a thumb position that contradicts the live level.
 */
const LEVEL_STEP = 5;

export interface Color3DToolButtonProps {
    isActive: boolean;
    onActivate: () => void;
    icon: ComponentType<{ className?: string }>;
    description: string;
}

/**
 * 3D Colors split-button: the left button activates the drag tool (horizontal =
 * level, vertical = window); the chevron opens a popover with the selectable
 * presets, the Endosize level slider and a reset action. The window is not
 * exposed here — the vertical drag is the only way to change it, as on desktop.
 *
 * No HU figures are shown: the rendered volume is the feedback that matters, and
 * the slider thumb already carries the level's position within its range.
 *
 * Unlike the 2D W/L popover there is no hover preview — previewing here would
 * mean rebuilding the volume's transfer functions on every pointer move, so
 * presets apply on click. A popover (not a dropdown menu) is used because the
 * menu primitive roves focus between its items and won't let the slider hold
 * the cursor.
 */
export function Color3DToolButton({ isActive, onActivate, icon: Icon, description }: Color3DToolButtonProps) {
    const { getServiceManager } = useVisualization();
    const { presetId, windowLevel, isAdjusted } = useColor3D();
    const [open, setOpen] = useState(false);

    const getService = useCallback(
        () => getServiceManager().getService<Color3DService>(Color3DService.SERVICE_NAME),
        [getServiceManager]
    );

    const handleLevelChange = useCallback(
        (values: number[]) => {
            if (values.length === 0) return;
            getService().setLevel(values[0]);
        },
        [getService]
    );

    const handlePreset = useCallback(
        (id: Color3DPresetId) => {
            getService().applyPreset(id);
            setOpen(false);
        },
        [getService]
    );

    const handleReset = useCallback(() => getService().resetWindowLevel(), [getService]);

    const level = Math.round(windowLevel.level);

    return (
        <div className="flex items-center">
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant={isActive ? 'default' : 'ghost'}
                        size="icon-sm"
                        onClick={onActivate}
                        className={cn(
                            'h-7 w-7 rounded-r-none',
                            !isActive && 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <Icon className="h-3.5 w-3.5" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">{description}</TooltipContent>
            </Tooltip>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant={isActive ? 'default' : 'ghost'}
                        size="icon-sm"
                        className={cn(
                            'h-7 w-4 rounded-l-none border-l-0 px-0',
                            !isActive && 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        <ChevronDown className="h-3 w-3" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    side="bottom"
                    align="start"
                    className="w-60 p-1"
                    // The viewport auto-focuses itself on pointerenter (for keydown shortcuts),
                    // which steals focus from the popover the moment the cursor leaves it. Keep
                    // the popover open on focus-out; a real outside click still dismisses it.
                    onFocusOutside={(e) => e.preventDefault()}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                >
                    <div className="px-2 py-1.5 text-sm font-semibold">Presets</div>
                    {getSelectableColor3DPresets().map((preset) => (
                        <button
                            key={preset.id}
                            type="button"
                            onClick={() => handlePreset(preset.id)}
                            className={cn(
                                'flex w-full items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent/50 focus-visible:bg-accent/50',
                                presetId === preset.id && 'bg-accent text-accent-foreground'
                            )}
                        >
                            <span>{preset.label}</span>
                        </button>
                    ))}
                    <div className="-mx-1 my-1 h-px bg-muted" />
                    <div className="px-2 py-1.5 text-sm font-semibold">Level</div>
                    <div className="px-2 pb-1.5">
                        <Slider
                            className="**:data-[slot=slider-track]:h-1.5 **:data-[slot=slider-thumb]:size-4"
                            min={COLOR_3D_LEVEL_RANGE.min}
                            max={COLOR_3D_LEVEL_RANGE.max}
                            step={LEVEL_STEP}
                            value={[level]}
                            onValueChange={handleLevelChange}
                        />
                    </div>
                    <div className="-mx-1 my-1 h-px bg-muted" />
                    <button
                        type="button"
                        onClick={handleReset}
                        disabled={!isAdjusted}
                        className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent/50 focus-visible:bg-accent/50 disabled:pointer-events-none disabled:opacity-50"
                    >
                        Reset to default
                    </button>
                </PopoverContent>
            </Popover>
        </div>
    );
}
